import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { 
  CheckCircle2, 
  Clock, 
  FileSearch, 
  MessageSquare, 
  Hammer,
  Shield,
  MousePointerClick,
  ListChecks,
  MapPin,
  Briefcase,
  ArrowRight
} from "lucide-react";

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const requestType = searchParams.get("type") || "your request";

  const steps = [
    {
      icon: FileSearch,
      title: "Quick review",
      description: "We read your request + current site if provided",
    },
    {
      icon: MessageSquare,
      title: "Fit call or questions",
      description: "We confirm goals, services, service area, and what you want built",
    },
    {
      icon: Hammer,
      title: "Build plan + timeline",
      description: "10–14 days once we have what we need",
    },
  ];

  const benefits = [
    { icon: Shield, text: "Builds trust instantly" },
    { icon: MousePointerClick, text: "Makes it easy to request a quote" },
    { icon: ListChecks, text: "Shows your services clearly" },
    { icon: MapPin, text: "Helps you rank locally (service + service-area structure)" },
    { icon: Briefcase, text: "Works 24/7 while you're on jobs" },
  ];

  const helpfulLinks = [
    { label: "Website Design", href: "/websites" },
    { label: "Lawn Care Website Design", href: "/lawn-care-website-design" },
    { label: "Landscaping Website Design", href: "/landscaping-website-design" },
  ];

  return (
    <>
      <Helmet>
        <title>Thank You | Leaf Rush Marketing</title>
        <meta name="description" content="Thank you for your inquiry. We'll be in touch within 1 business day." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://www.leafrushmarketing.com/thank-you" />
      </Helmet>

      <div className="flex flex-col">
        {/* Hero Section */}
        <section className="py-16 md:py-24">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="h-10 w-10 text-primary" />
                </div>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight">
                You're all set — we got your request.
              </h1>
              <p className="text-xl text-muted-foreground">
                Next step: we'll reply within 1 business day to confirm details and your exact monthly rate.
              </p>
            </div>
          </div>
        </section>

        {/* What Happens Next */}
        <section className="py-12 bg-accent/20">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
                What happens next
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {steps.map((step, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6 text-center">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <step.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-sm font-medium text-primary mb-2">
                        Step {index + 1}
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                      <p className="text-muted-foreground text-sm">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why a Strong Website Matters */}
        <section className="py-12">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
                Why a strong website matters
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-4 rounded-lg bg-accent/30"
                  >
                    <benefit.icon className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Primary CTA */}
        <section className="py-8">
          <div className="container px-4 md:px-6">
            <div className="max-w-xl mx-auto text-center">
              <Link to="/websites">
                <Button size="lg" className="gap-2">
                  Back to Website Design
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Prefer to Talk Now */}
        <section className="py-12 bg-muted/50">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Prefer to talk now?
              </h2>
              <p className="text-muted-foreground">
                Schedule a call instead.
              </p>
            </div>
            <CalendlyEmbed variant="minimal" />
          </div>
        </section>

        {/* In the Meantime */}
        <section className="py-12">
          <div className="container px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-xl font-semibold mb-6">In the meantime</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {helpfulLinks.map((link, index) => (
                  <Link key={index} to={link.href}>
                    <Button variant="outline" size="sm">
                      {link.label}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Response Time */}
        <section className="py-8 border-t">
          <div className="container px-4 md:px-6">
            <div className="max-w-xl mx-auto text-center flex items-center justify-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="text-sm">
                Typical response: within 2 hours during business hours
              </span>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ThankYou;
